import React from "react";
import { PhoneType, RoleUser } from "@homesusa/core";
import {
  FormContext,
  FormContextProps,
  FormRules,
  useGetFormErrors,
  useFormRefs
} from "@homesusa/form";
import { AppContext } from "@homesusa/layout";
import { createUser } from "core/services/user.service";

export const initialNewUserValues = {
  firstName: "",
  lastName: "",
  email: "",
  businessPhone: "",
  mobilePhone: "",
  role: RoleUser.User as string
};

export const newUserRules: FormRules = {
  firstName: { required: true, label: "First name" },
  lastName: { required: true, label: "Last name" },
  email: {
    required: true,
    label: "Email",
    regex: "^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$",
    regexMessage: "Invalid email address"
  },
  businessPhone: {
    label: "Business phone",
    regex: "^\\d{10}$",
    regexMessage: "Invalid phone"
  },
  mobilePhone: {
    label: "Mobile phone",
    regex: "^\\d{10}$",
    regexMessage: "Invalid phone"
  },
  role: { required: true, label: "Role" }
};

export function useNewUserForm({
  onClose,
  onCreated
}: {
  onClose: () => void;
  onCreated: () => void;
}): {
  saving: boolean;
  confirmDiscard: boolean;
  error: string;
  canSubmit: boolean;
  requestClose: () => void;
  keepEditing: () => void;
  submit: () => Promise<void>;
} {
  const { formState } =
    React.useContext<FormContextProps<typeof initialNewUserValues>>(
      FormContext
    );
  const { getFormErrorsProps } = useGetFormErrors();
  const { focusField } = useFormRefs();
  const { addAlert } = React.useContext(AppContext);
  const [saving, setSaving] = React.useState(false);
  const savingRef = React.useRef(false);
  const [confirmDiscard, setConfirmDiscard] = React.useState(false);
  const [error, setError] = React.useState("");
  const dirty = Object.entries(initialNewUserValues).some(
    ([name, value]) => formState[name as keyof typeof formState] !== value
  );
  const canSubmit = [
    formState.firstName,
    formState.lastName,
    formState.email
  ].every((value) => value.trim().length > 0);

  const requestClose = (): void => {
    if (savingRef.current) return;
    if (confirmDiscard) {
      setConfirmDiscard(false);
    } else if (dirty) {
      setConfirmDiscard(true);
    } else {
      onClose();
    }
  };

  const submit = async (): Promise<void> => {
    if (savingRef.current || confirmDiscard) return;
    const invalidFields = getFormErrorsProps(formState, newUserRules);
    if (invalidFields.length > 0) {
      setError(
        "Please correct the highlighted fields before creating the user."
      );
      void focusField(invalidFields[0]);
      return;
    }

    savingRef.current = true;
    setSaving(true);
    setError("");
    try {
      await createUser({
        firstName: formState.firstName,
        lastName: formState.lastName,
        email: formState.email,
        roles: [formState.role],
        phones: [
          { phoneType: PhoneType.Business, number: formState.businessPhone },
          { phoneType: PhoneType.Mobile, number: formState.mobilePhone }
        ].filter(({ number }) => number.length > 0)
      });
    } catch {
      const message = "We couldn't create the user. Please try again.";
      setError(message);
      addAlert({ message, variant: "error" });
      return;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
    addAlert({ message: "User created successfully.", variant: "success" });
    onClose();
    onCreated();
  };

  return {
    saving,
    confirmDiscard,
    error,
    canSubmit,
    requestClose,
    keepEditing: (): void => setConfirmDiscard(false),
    submit
  };
}
